// A run's Landed cost (Mitch, 2026-11-04: "I don't really understand the purpose of actual vs plan … at the end of the
// day we are interested in the cost of a run, and the actual landed price, so that we can properly input the cost on our
// website for the product"): what the run cost, in plain words, then one row per product with its units, what one unit
// landed at (everything in, its make-up on hover), Shopify's cost now and Set Shopify cost. Before anything is built a
// unit's cost is the plan's estimate, marked so, and can't be set. No plan against actual, variance or MSRP here.

import { RefreshCw, Upload } from "lucide-react";
import { useState } from "react";
import { type RunLandedRow, runLandedCents, runLandedRows, runResult } from "../../../../../shared/costs.ts";
import { centsText, shortDate, signedCents, useCostIndex } from "../../../lib/costs.ts";
import { money, qty } from "../../../lib/format.ts";
import { can, mutate, mutateWithResult } from "../../../lib/mutate.ts";
import { Button, cx } from "../../../ui/controls.tsx";
import { CellControls, type Column, DataTable } from "../../../ui/DataTable.tsx";
import { Figure, Figures } from "../../../ui/PageParts.tsx";
import { toast } from "../../../ui/toast.tsx";
import { Why } from "../../../ui/Why.tsx";
import { Card } from "../Workspace.tsx";
import { stackLines } from "./RunPlan.tsx";
import type { RunProps } from "./RunsTab.tsx";

type Row = RunLandedRow & { id: string };

export function LandedCostTab({ run }: RunProps) {
  const cx_ = useCostIndex();
  const res = runResult(cx_, run.id);
  const [slot, setSlot] = useState<HTMLDivElement | null>(null); // Table views --- by the card's title
  const [busy, setBusy] = useState(false);
  if (!res) return null;
  const rows: Row[] = runLandedRows(cx_, run.id).map((r) => ({ ...r, id: r.item_id }));
  const t = res.totals;
  const units = runLandedCents(rows);
  // Spent, less what's on the units you sell, run goods and shipping to backers: parts bought and not used yet (left at
  // the CM for later runs: boards built and not used too), units ordered but not built, tooling another run carries (net
  // of parts used from earlier stock).
  const left = t.spentCents - t.runGoodsCents - t.presaleCents - units;
  const final = run.status === "closed" || run.historical;
  const anyBuilt = rows.some((r) => r.built);
  const toSet = rows.filter((r) => r.settable && !r.same);
  const editable = can("costs.set_shopify_from_run");
  const setCosts = async (pick: Row[]) => {
    if (!pick.length) return;
    const what = pick.length === 1 ? `${pick[0].sku}'s` : `${pick.length} products'`;
    if (
      !confirm(
        `Set ${what} cost in Shopify to this run's landed cost?${final ? "" : " The run isn't closed yet, so later bills (freight, duty) can still change it."}`,
      )
    )
      return;
    setBusy(true);
    const { ok } = await mutateWithResult("costs.set_shopify_from_run", {
      run_id: run.id,
      costs: pick.map((r) => ({ item_id: r.item_id, std_cost: r.next as number })),
    });
    setBusy(false);
    if (ok) toast.ok(`${pick.length === 1 ? `${pick[0].sku}'s cost` : `${pick.length} costs`} queued for Shopify (Sell items shows when they've gone)`);
  };
  const columns: Column<Row>[] = [
    { key: "sku", header: "SKU", width: "minmax(110px,140px)", cell: (r) => <span className="truncate font-mono text-[12px]">{r.sku}</span> },
    { key: "label", header: "Product", width: "minmax(160px,1fr)", cell: (r) => <span className="truncate">{r.label}</span> },
    {
      key: "good",
      header: "Units",
      headerTitle: "The units you sell: built less set aside, rejected and scrapped (the plan's until some are built)",
      width: "90px",
      align: "right",
      value: (r) => r.units?.good ?? 0,
      cell: (r) => (
        <span className={cx("font-mono text-[12px]", !r.built && "text-faint")}>
          {qty(r.units?.good ?? 0)}
          {!r.built && "*"}
        </span>
      ),
      total: (rs) => <span className="font-mono text-[12px]">{qty(rs.reduce((n, r) => n + (r.built ? (r.units?.good ?? 0) : 0), 0))}</span>,
    },
    {
      key: "unit",
      header: "Landed cost a unit",
      headerTitle: "Everything in: parts, the maker's work, packaging, freight and duty, the run's share of tooling. Hover for its make-up",
      width: "130px",
      align: "right",
      value: (r) => r.next,
      text: (r) => (r.next == null ? "" : r.next),
      cell: (r) =>
        r.stack ? (
          <span className={cx("font-mono text-[12px]", r.built ? "font-medium" : "text-muted")}>
            <Why
              title={`${r.sku}: ${r.built ? "landed cost a unit" : "estimated (nothing built yet)"}`}
              lines={stackLines(r.stack)}
              note={[
                r.built ? `Over ${qty(r.units?.good ?? 0)} units` : "Nothing built yet: the plan's estimate, from today's quotes and the BOM",
                ...(r.built && !r.stack.complete ? ["Some costs aren't known yet (a part with no price), so it can't be set in Shopify"] : []),
                ...r.stack.flags,
              ].join(" · ")}
              width={440}
            >
              {money(r.next ?? 0)}
              {!r.built && " est."}
            </Why>
          </span>
        ) : (
          <span className="text-[12px] text-faint">no cost yet</span>
        ),
    },
    {
      key: "current",
      header: "Shopify cost now",
      width: "120px",
      align: "right",
      value: (r) => r.current,
      cell: (r) => <span className="font-mono text-[12px]">{r.current == null ? <span className="text-faint">none</span> : money(r.current)}</span>,
    },
    {
      key: "change",
      header: "Change",
      width: "90px",
      align: "right",
      value: (r) => (r.settable && r.current != null && r.next != null ? Math.round((r.next - r.current) * 100) : null),
      text: (r) => (!r.settable ? "" : r.same ? "same" : r.current == null ? "new" : signedCents(Math.round(((r.next ?? 0) - r.current) * 100))),
      cell: (r) =>
        !r.settable ? null : (
          <span className={cx("font-mono text-[12px]", r.same && "text-faint")}>
            {r.same ? "same" : r.current == null ? "new" : signedCents(Math.round(((r.next ?? 0) - r.current) * 100))}
          </span>
        ),
    },
    ...(editable
      ? [
          {
            key: "set",
            header: "",
            width: "130px",
            value: () => "",
            cell: (r: Row) =>
              r.settable && !r.same ? (
                <CellControls>
                  <Button size="sm" disabled={busy} onClick={() => setCosts([r])} title={`Shopify's cost for ${r.sku} becomes ${money(r.next ?? 0)}`}>
                    Set Shopify cost
                  </Button>
                </CellControls>
              ) : r.settable ? (
                <span className="text-[11px] text-faint">Shopify has it</span>
              ) : null,
          } satisfies Column<Row>,
        ]
      : []),
  ];
  const basis = (run.plan_basis as { at?: string })?.at;
  return (
    <>
      <Figures>
        <Figure
          label={final ? "Cost of the units" : "Cost of the units so far"}
          value={
            <Why
              title="Cost of the units"
              note={`What the units you sell cost all together: each product's landed cost a unit × its units${t.setAsideUnits ? `. It includes the ${qty(t.setAsideUnits)} set aside (${centsText(t.setAsideCents)}), carried by the units kept` : ""}${final ? "" : ". Until the run is closed it's what's known so far"}`}
            >
              {centsText(units)}
            </Why>
          }
          sub={`${qty(rows.reduce((n, r) => n + (r.units?.good ?? 0), 0))} units`}
        />
        <Figure
          label="Spent on the run"
          value={
            <Why title="Spent on the run" note="Every PO line on the run's POs, their landed costs, the run's own costs and tooling it paid for">
              {centsText(t.spentCents)}
            </Why>
          }
        />
        {left > 0 ? (
          <Figure
            label="Left over for later runs"
            value={
              <Why
                title="Left over for later runs"
                note="Spent on the run but not in these units: parts bought and not used (spares left at the CM), units ordered and not built, tooling another run carries"
              >
                {centsText(left)}
              </Why>
            }
          />
        ) : left < 0 ? (
          <Figure
            label="From earlier stock"
            value={
              <Why
                title="From earlier stock"
                note="In these units but not spent on this run: parts used from stock bought before it, tooling another run paid for"
              >
                {centsText(-left)}
              </Why>
            }
          />
        ) : null}
        {t.runGoodsCents + t.presaleCents > 0 && (
          <Figure
            label="Other things it paid for"
            value={
              <Why title="Other things it paid for" note="Run goods (cables for backers…) and shipping to backers: in what it spent, never in a unit's cost">
                {centsText(t.runGoodsCents + t.presaleCents)}
              </Why>
            }
          />
        )}
      </Figures>
      <Card
        boxed={false}
        title="Landed cost per product"
        note={
          !anyBuilt
            ? "Nothing built yet, so these are the plan's estimates; each firms up as units are built and the invoices come in"
            : final
              ? "What one unit cost, everything in: set it as the product's cost in Shopify"
              : "What one unit cost so far: freight, duty and other bills that come later can still change it until the run is closed"
        }
        actions={
          <>
            <div ref={setSlot} className="flex items-center gap-1" />
            {can("production_run.replan") && !run.historical && run.status !== "closed" && basis && !anyBuilt && (
              <Button
                size="sm"
                variant="ghost"
                icon={<RefreshCw className="size-3.5" />}
                title={`The estimates use the quotes and rates of ${shortDate(basis)}`}
                onClick={() => confirm("Work the estimates out again with today's quotes and rates?") && mutate("production_run.replan", { id: run.id })}
              >
                Use today's prices
              </Button>
            )}
            {editable && toSet.length > 1 && (
              <Button size="sm" variant="primary" icon={<Upload className="size-3.5" />} disabled={busy} onClick={() => setCosts(toSet)}>
                Set all {toSet.length} in Shopify
              </Button>
            )}
          </>
        }
      >
        <DataTable
          fit={24}
          rows={rows}
          columns={columns}
          exportName="Run landed cost"
          viewsSlot={slot}
          pageFilters={false}
          rowNoun={["product", "products"]}
          empty="No units yet: type them on the Units tab."
        />
        {(rows.some((r) => !r.built) || res.flags.length > 0) && (
          <div className="flex flex-col gap-0.5 border-t border-line px-4 py-2 text-[11px] text-faint">
            {rows.some((r) => !r.built) && <span>* nothing built yet: the units planned, at the plan's estimated cost</span>}
            {res.flags
              .filter((f) => !/plan wasn't frozen/.test(f))
              .map((f) => (
                <span key={f}>{f}</span>
              ))}
          </div>
        )}
      </Card>
    </>
  );
}
